import type { SlideshowTransition } from './infoserviceSlideshowApi.js'
import { easingCss } from './transitionEasing.js'

/**
 * Aperçu animé d'une transition du diaporama (Réglages › Transitions).
 *
 * Deux images factices, A et B, se relaient : à chaque étape, l'image du
 * dessus est remplacée par l'autre selon la transition réglée, à sa vraie
 * durée et dans son vrai sens. Le sens suit CasparCG 2.5 (vérifié à l'image) :
 * `LEFT`, la nouvelle image entre par la droite et file vers la gauche.
 */

/** Temps de pose d'une image avant la transition suivante. */
export const PREVIEW_HOLD_MS = 1400
/** Durée montrée pour une transition sans durée propre (coupe). */
const CUT_PREVIEW_MS = 1

export interface TransitionPreviewSpec {
  /** Animation de l'image qui arrive (posée au-dessus). */
  incoming: string
  /** Animation de l'image qui part, ou `null` si elle reste immobile dessous. */
  outgoing: string | null
  /** Durée de la transition elle-même, en ms. */
  durationMs: number
  /** Stinger : instant de la coupe A → B sous le stinger, en ms. */
  cutMs: number | null
  /** Courbe du mouvement (CSS), celle que jouera CasparCG. */
  timing: string
}

export function transitionPreviewSpec(transition: SlideshowTransition): TransitionPreviewSpec {
  if (transition.kind === 'STINGER') {
    const durationMs = Math.max(1, transition.durationMs)
    return {
      incoming:   'slideshow-preview-appear',
      outgoing:   null,
      durationMs,
      cutMs:      Math.min(Math.max(0, transition.cutMs), durationMs),
      timing:     'linear',
    }
  }
  const side = transition.direction === 'RIGHT' ? 'right' : 'left'
  const durationMs = transition.type === 'CUT' || transition.durationMs <= 0 ? CUT_PREVIEW_MS : transition.durationMs
  const timing = easingCss(transition.easing ?? 'LINEAR')
  switch (transition.type) {
    case 'MIX':
      return { incoming: 'slideshow-preview-fade-in', outgoing: null, durationMs, cutMs: null, timing }
    case 'WIPE':
      return { incoming: `slideshow-preview-wipe-${side}`, outgoing: null, durationMs, cutMs: null, timing }
    case 'SLIDE':
      return { incoming: `slideshow-preview-enter-${side}`, outgoing: null, durationMs, cutMs: null, timing }
    case 'PUSH':
      return { incoming: `slideshow-preview-enter-${side}`, outgoing: `slideshow-preview-exit-${side}`, durationMs, cutMs: null, timing }
    case 'CUT':
      return { incoming: 'slideshow-preview-appear', outgoing: null, durationMs, cutMs: null, timing: 'linear' }
  }
}

/** Durée d'une étape complète : pose de l'image, puis transition. */
export function previewStepMs(spec: TransitionPreviewSpec): number {
  return PREVIEW_HOLD_MS + spec.durationMs
}

/**
 * Instant (ms dans la transition) montré quand l'aperçu est à l'arrêt : le
 * milieu du mouvement, pour qu'une vignette immobile dise déjà ce qu'elle fait.
 * Stinger : avant la coupe, quand il commence à couvrir l'image A.
 */
export function previewStillMs(spec: TransitionPreviewSpec): number {
  if (spec.cutMs !== null) return Math.round(spec.cutMs * 0.6)
  return Math.round(spec.durationMs / 2)
}
